import { Trans, useTranslation } from 'react-i18next';
import { Price } from '@/components/Price/Price';
import SummaryRow from '@/components/Summary/SummaryRow';
import SummaryTotal from '@/components/Summary/SummaryTotal';
import { calculateSinglePersonTip } from '@/utils/tipCalculations';
import styles from './Summary.module.scss';

interface Props {
  baseToPay: number;
  serviceFee?: number;
  tipPercentage: number;
  grossDiscount: number;
  onToggleServiceFeeInfo?: () => void;
  customTipAmount?: number | null;
}

export const Summary = ({
  serviceFee,
  grossDiscount,
  baseToPay,
  tipPercentage,
  onToggleServiceFeeInfo,
  customTipAmount,
}: Props) => {
  const { t } = useTranslation();
  const tipToPay = calculateSinglePersonTip(customTipAmount, baseToPay, tipPercentage);
  const totalAmount = baseToPay + tipToPay + (serviceFee || 0);

  return (
    <div className={styles.root}>
      <h2 data-testid="payment-summary-header">{t('checkout.summary.paymentSummary')}</h2>

      <SummaryRow
        label={t('checkout.summary.subtotal')}
        value={<Price amount={baseToPay + grossDiscount} />}
        testId="subtotal"
      />

      {grossDiscount > 0 && (
        <SummaryRow
          discount
          label={t('checkout.summary.discount')}
          value={
            <Trans
              key={String(grossDiscount)}
              i18nKey="checkout.summary.discountPrice"
              components={{ price: <Price amount={grossDiscount} /> }}
            />
          }
          testId="discount"
        />
      )}

      <SummaryRow
        label={t('checkout.summary.tips')}
        value={<Price amount={tipToPay} />}
        testId="summary-tips"
      />

      {serviceFee !== undefined && serviceFee !== 0 && (
        <SummaryRow
          label={t('checkout.summary.serviceFee')}
          value={<Price amount={serviceFee} />}
          onClick={onToggleServiceFeeInfo}
          testId="service-fee"
        />
      )}

      <SummaryTotal
        label={t('checkout.summary.totalAmount')}
        value={<Price amount={totalAmount} />}
        testId="total"
      />
    </div>
  );
};
